import React from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Mail, 
  MapPin, 
  Phone,
  Github,
  Linkedin,
  Youtube,
  Twitter,
  Facebook,
  FileText,
  ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import NewsletterSignup from './NewsletterSignup';

const ComprehensiveFooter = () => {
  const footerSections = {
    company: {
      title: "Company",
      links: [
        { label: "About Us", href: "#about-us" },
        { label: "Vision", href: "#our-vision" },
        { label: "Leadership", href: "#our-team" },
        { label: "Careers", href: "https://forms.gle/85WmvhX9xBAtUiJH7", external: true },
        { label: "Investors", href: "https://forms.gle/ujBE7meWTSj9tjnm7", external: true },
        { label: "Press Kit", href: "#press-kit" }
      ]
    },
    products: {
      title: "Products",
      links: [
        { 
          label: "Hardware", 
          subitems: [
            { label: "QubitPulsing™ - Pulse Processing Unit", href: "#quantum-hardware" },
            { label: "QubitFabrica™ - CPU/GPU/QPU Scheduler", href: "#quantum-hardware" }
          ]
        },
        { 
          label: "Software", 
          subitems: [
            { label: "Control System", href: "#quantum-platform" },
            { label: "GUI", href: "#quantum-platform" },
            { label: "APIs", href: "#quantum-platform" }
          ]
        }
      ]
    },
    platform: {
      title: "Platform",
      links: [
        { label: "QubitHub™ - Hyperscaler Global DCaaS", href: "#quantum-platform" },
        { label: "QubitCali™ - Superconducting Qubit Calibration Suite", href: "#quantum-platform" },
        { label: "QubitDeviG™ - Device Gateway", href: "#quantum-platform" },
        { label: "QubitEngine™ - Core Engine", href: "#quantum-platform" },
        { label: "QubitTrans™ - Polymath LangChain Framework", href: "#quantum-platform" }
      ]
    },
    useCases: {
      title: "Use Cases",
      links: [
        { label: "Energy: Oil & Gas Exploration", href: "/use-cases/energy" },
        { label: "Finance & Trading", href: "/use-cases/finance" },
        { label: "AI Infrastructure", href: "/use-cases/ai" },
        { label: "Healthcare", href: "/use-cases/healthcare" },
        { label: "Research", href: "/use-cases/research" }
      ]
    },
    resources: {
      title: "Resources",
      links: [
        { label: "Documentation", href: "#documentation" },
        { label: "Scientific Publications", href: "#publications" },
        { label: "Blog", href: "https://medium.com/@aruns89", external: true },
        { label: "Videos", href: "#quantum-videos" },
        { label: "Webinars", href: "#quantum-webinars" }
      ]
    },
    support: {
      title: "Support",
      links: [
        { label: "FAQs", href: "#faq" },
        { label: "Community Discord", href: "https://discord.gg/aNKPwwBy", external: true },
        { label: "Technical Support", href: "#tech-support" },
        { label: "Training", href: "#training" }
      ]
    }
  };

  const socialMedia = [
    { 
      name: "GitHub", 
      icon: Github, 
      href: "https://github.com/godquantum-labs",
      real: true
    },
    { 
      name: "LinkedIn", 
      icon: Linkedin, 
      href: "https://linkedin.com/company/godquantum-systems",
      real: false
    },
    { 
      name: "YouTube", 
      icon: Youtube, 
      href: "https://youtube.com/@godquantum-systems",
      real: false
    },
    { 
      name: "X (Twitter)", 
      icon: Twitter, 
      href: "https://x.com/godquantum",
      real: false
    },
    { 
      name: "Facebook", 
      icon: Facebook, 
      href: "https://facebook.com/godquantum",
      real: false
    }
  ];

  const legalLinks = [
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Responsible Disclosure", href: "/responsible-disclosure" }
  ];

  return (
    <footer className="bg-black border-t border-white/10 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/5 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_center,rgba(0,255,209,0.03),transparent_70%)]"></div>

      <div className="max-w-7xl mx-auto px-4 py-16 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-8 mb-12">
          
          {/* Company Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {footerSections.company.title}
            </h3>
            <ul className="space-y-3">
              {footerSections.company.links.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-400 hover:text-emerald-300 transition-colors text-sm flex items-center group"
                  >
                    {link.label}
                    <ArrowRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Products Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {footerSections.products.title}
            </h3>
            <ul className="space-y-3">
              {footerSections.products.links.map((link, index) => (
                <li key={index}>
                  <div className="text-gray-300 font-medium text-sm mb-2">{link.label}</div>
                  {link.subitems && (
                    <ul className="ml-3 space-y-2">
                      {link.subitems.map((subitem, subIndex) => (
                        <li key={subIndex}>
                          <a 
                            href={subitem.href}
                            className="text-gray-400 hover:text-emerald-300 transition-colors text-xs flex items-center group"
                          >
                            {subitem.label}
                            <ArrowRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Platform Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {footerSections.platform.title}
            </h3>
            <ul className="space-y-3">
              {footerSections.platform.links.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-400 hover:text-emerald-300 transition-colors text-sm flex items-center group"
                  >
                    {link.label}
                    <ArrowRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Use Cases Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {footerSections.useCases.title}
            </h3>
            <ul className="space-y-3">
              {footerSections.useCases.links.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-400 hover:text-emerald-300 transition-colors text-sm flex items-center group"
                  >
                    {link.label}
                    <ArrowRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Resources & Support Combined */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="lg:col-span-2 xl:col-span-1"
          >
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {footerSections.resources.title}
            </h3>
            <ul className="space-y-3 mb-6">
              {footerSections.resources.links.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-400 hover:text-emerald-300 transition-colors text-sm flex items-center group"
                    target={link.external ? "_blank" : "_self"}
                    rel={link.external ? "noopener noreferrer" : ""}
                  >
                    {link.label}
                    {link.external ? (
                      <ExternalLink className="w-3 h-3 ml-1" />
                    ) : (
                      <ArrowRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                    )}
                  </a>
                </li>
              ))}
            </ul>

            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              {footerSections.support.title}
            </h3>
            <ul className="space-y-3">
              {footerSections.support.links.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-400 hover:text-emerald-300 transition-colors text-sm flex items-center group"
                    target={link.external ? "_blank" : "_self"}
                    rel={link.external ? "noopener noreferrer" : ""}
                  >
                    {link.label}
                    {link.external ? (
                      <ExternalLink className="w-3 h-3 ml-1" />
                    ) : (
                      <ArrowRight className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all" />
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Newsletter Signup */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
            className="lg:col-span-2 xl:col-span-1"
          >
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
              Stay Updated
            </h3>
            <p className="text-gray-400 text-sm mb-4 leading-relaxed">
              Get the latest quantum computing insights, research, and product updates delivered to your inbox.
            </p>
            
            {/* Enhanced Newsletter Signup */}
            <Card className="p-4 bg-white/5 border-white/10 backdrop-blur-sm">
              <NewsletterSignup />
            </Card>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-2"
            >
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Mail className="w-4 h-4" />
                <a href="mailto:contact@godquantum.tech" className="hover:text-emerald-300 transition-colors">
                  contact@godquantum.tech
                </a>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <MapPin className="w-4 h-4" />
                <span>Bengaluru, Karnataka, India</span>
              </div>
            </motion.div>

            {/* Social Media Links */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex items-center gap-4"
            >
              {socialMedia.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-2 rounded-lg transition-all duration-300 ${
                      social.real 
                        ? 'text-gray-400 hover:text-emerald-300 hover:bg-emerald-500/10' 
                        : 'text-gray-600 hover:text-gray-500 cursor-not-allowed opacity-50'
                    }`}
                    whileHover={social.real ? { scale: 1.1 } : {}}
                    whileTap={social.real ? { scale: 0.95 } : {}}
                    title={social.real ? social.name : `${social.name} (Coming Soon)`}
                    onClick={!social.real ? (e) => e.preventDefault() : undefined}
                  >
                    <IconComponent className="w-5 h-5" />
                  </motion.a>
                );
              })}
            </motion.div>
          </div>

          {/* Bottom Section - Logo + Copyright | Legal Links */}
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4 mt-8 pt-6 border-t border-white/5">
            
            {/* Logo + Copyright */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex items-center gap-3"
            >
              <div className="text-emerald-400 font-bold text-lg">Godquantum</div>
              <span className="text-gray-500 text-sm">© 2025 All rights reserved</span>
            </motion.div>

            {/* Legal Links - Simplified */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="flex items-center gap-2 text-gray-500 text-sm"
            >
              {legalLinks.map((link, index) => (
                <React.Fragment key={index}>
                  <a
                    href={link.href}
                    className="hover:text-emerald-300 transition-colors"
                  >
                    {link.label}
                  </a>
                  {index < legalLinks.length - 1 && <span className="text-gray-600">|</span>}
                </React.Fragment>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ComprehensiveFooter;